import type React from "react"
import { Link } from "react-router-dom"
import {
  Facebook,
  Twitter,
  Instagram,
  Youtube,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  Truck,
  Shield,
  RefreshCw,
} from "lucide-react"

const Footer: React.FC = () => {
  return (
    <footer className="bg-primary-900 text-white">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">A</span>
              </div>
              <div>
                <h2 className="text-xl font-bold">Anmol</h2>
                <p className="text-sm text-gray-300">Furniture Bazaar</p>
              </div>
            </div>
            <p className="text-gray-300 mb-4">
              Your trusted partner for premium furniture. Creating beautiful spaces that inspire and comfort for over a
              decade.
            </p>
            <div className="flex space-x-4">
              <a href="#" className="text-gray-300 hover:text-white transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-300 hover:text-white transition-colors">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-300 hover:text-white transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-300 hover:text-white transition-colors">
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/about" className="text-gray-300 hover:text-white transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-300 hover:text-white transition-colors">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/shipping" className="text-gray-300 hover:text-white transition-colors">
                  Shipping Info
                </Link>
              </li>
              <li>
                <Link to="/returns" className="text-gray-300 hover:text-white transition-colors">
                  Returns & Exchanges
                </Link>
              </li>
              <li>
                <Link to="/warranty" className="text-gray-300 hover:text-white transition-colors">
                  Warranty
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-gray-300 hover:text-white transition-colors">
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/size-guide" className="text-gray-300 hover:text-white transition-colors">
                  Size Guide
                </Link>
              </li>
            </ul>
          </div>

          {/* Categories */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Shop by Category</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/category/beds" className="text-gray-300 hover:text-white transition-colors">
                  Beds & Mattresses
                </Link>
              </li>
              <li>
                <Link to="/category/sofas" className="text-gray-300 hover:text-white transition-colors">
                  Sofas & Seating
                </Link>
              </li>
              <li>
                <Link to="/category/dining" className="text-gray-300 hover:text-white transition-colors">
                  Dining Furniture
                </Link>
              </li>
              <li>
                <Link to="/category/wardrobes" className="text-gray-300 hover:text-white transition-colors">
                  Wardrobes & Storage
                </Link>
              </li>
              <li>
                <Link to="/category/chairs" className="text-gray-300 hover:text-white transition-colors">
                  Chairs & Stools
                </Link>
              </li>
              <li>
                <Link to="/category/tables" className="text-gray-300 hover:text-white transition-colors">
                  Tables & Desks
                </Link>
              </li>
              <li>
                <Link to="/category/modular" className="text-gray-300 hover:text-white transition-colors">
                  Modular Furniture
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Get in Touch</h3>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-primary-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-300">123 Furniture Street</p>
                  <p className="text-gray-300">Mumbai, Maharashtra 400001</p>
                  <p className="text-gray-300">India</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-primary-400" />
                <div>
                  <p className="text-gray-300">+91 98765 43210</p>
                  <p className="text-gray-300">+91 87654 32109</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-primary-400" />
                <div>
                  <p className="text-gray-300">info@anmolfurniture.com</p>
                  <p className="text-gray-300">support@anmolfurniture.com</p>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="mt-6">
              <h4 className="font-semibold mb-2 text-primary-200">Business Hours</h4>
              <div className="text-sm text-gray-300 space-y-1">
                <p>Mon - Sat: 9:00 AM - 8:00 PM</p>
                <p>Sunday: 10:00 AM - 6:00 PM</p>
              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="border-t border-primary-800 mt-8 pt-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="flex items-center space-x-3">
              <Truck className="w-8 h-8 text-primary-400" />
              <div>
                <h4 className="font-semibold text-primary-200">Free Delivery</h4>
                <p className="text-sm text-gray-300">On orders above ₹25,000</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Shield className="w-8 h-8 text-primary-400" />
              <div>
                <h4 className="font-semibold text-primary-200">Warranty</h4>
                <p className="text-sm text-gray-300">Up to 3 years coverage</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <RefreshCw className="w-8 h-8 text-primary-400" />
              <div>
                <h4 className="font-semibold text-primary-200">Easy Returns</h4>
                <p className="text-sm text-gray-300">30-day return policy</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <CreditCard className="w-8 h-8 text-primary-400" />
              <div>
                <h4 className="font-semibold text-primary-200">Secure Payment</h4>
                <p className="text-sm text-gray-300">Multiple payment options</p>
              </div>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-primary-800 mt-8 pt-8 text-center text-gray-300">
          <p>&copy; {new Date().getFullYear()} Anmol Furniture. All rights reserved.</p>
        </div>
      </div>
    </footer>
  )
}

export default Footer
